import { TypeOfConsideration } from '../pricing/models/type-of-consideration.enum';
import { CorporateEquityType, DeferredVariablePaymentType } from '../loi-form/term-form';
import { LoiComparisonFormDto } from './models/loi-comparison-form.model';
import { findDfpSummaryRow, findDvpSummaryRow } from '../loi-form/payment-rows';

// Mirrors the fairValue cell derivation in LoiComparisonRows.tsx's build*Row
// functions — keep the two in sync when purchasePriceSummary shape changes.
export const getFairValuesByType = (
    dto: LoiComparisonFormDto,
): Partial<Record<TypeOfConsideration, number | null>> => {
    const summary = dto.form?.termForm?.purchasePriceSummary;
    if (!summary) {
        return {};
    }
    const dvpRows = summary.deferredVariablePayments?.rows ?? [];
    const earnout = findDvpSummaryRow(dvpRows, DeferredVariablePaymentType.Earnout);
    const synthetic = findDvpSummaryRow(dvpRows, DeferredVariablePaymentType.Synthetic);
    const dfpRow = findDfpSummaryRow(summary.deferredFixedPayments?.rows ?? []);
    const corpRows = summary.corporateEquity?.rows ?? [];
    const preferred = corpRows.find((row) => row.type === CorporateEquityType.Preferred);
    const common = corpRows.find((row) => row.type === CorporateEquityType.Common);

    return {
        [TypeOfConsideration.CashAtClose]: summary.cashAtCloser?.fairValue ?? null,
        [TypeOfConsideration.DeferredFixedPayments]:
            dfpRow?.fairValue ?? null,
        [TypeOfConsideration.DeferredVariablePayments]: null,
        [TypeOfConsideration.Earnout]: earnout?.fairValue ?? null,
        [TypeOfConsideration.SyntheticJvEquity]: synthetic?.fairValue ?? null,
        [TypeOfConsideration.JvEquity]: summary.jvEquity?.row?.fairValue ?? null,
        [TypeOfConsideration.CorporateEquity]: null,
        [TypeOfConsideration.PreferredEquity]: preferred?.fairValue ?? null,
        [TypeOfConsideration.CommonEquity]: common?.fairValue ?? null,
        [TypeOfConsideration.Other]: summary.otherFairValue ?? null,
    };
};

function sumOrNull(values: (number | null | undefined)[]): number | null {
    if (values.length === 0) return null;
    return values.reduce((total: number, value) => total + (value ?? 0), 0);
}

// Mirrors the faceValue cell derivation in LoiComparisonRows.tsx's build*Row
// functions — keep the two in sync when purchasePriceSummary shape changes.
export const getFaceValuesByType = (
    dto: LoiComparisonFormDto,
): Partial<Record<TypeOfConsideration, number | null>> => {
    const summary = dto.form?.termForm?.purchasePriceSummary;
    if (!summary) {
        return {};
    }
    const dvpRows = summary.deferredVariablePayments?.rows ?? [];
    const dfpRows = summary.deferredFixedPayments?.rows ?? [];
    const corpRows = summary.corporateEquity?.rows ?? [];
    const preferred = corpRows.find((row) => row.type === CorporateEquityType.Preferred);
    const common = corpRows.find((row) => row.type === CorporateEquityType.Common);

    return {
        [TypeOfConsideration.CashAtClose]: summary.cashAtCloser?.cashAtClose ?? null,
        [TypeOfConsideration.DeferredFixedPayments]: sumOrNull(dfpRows.map((row) => row.amount)),
        [TypeOfConsideration.DeferredVariablePayments]: null,
        [TypeOfConsideration.Earnout]: sumOrNull(
            dvpRows.filter((row) => row.type === DeferredVariablePaymentType.Earnout)
                .map((row) => row.valueAtClosing)
        ),
        [TypeOfConsideration.SyntheticJvEquity]: sumOrNull(
            dvpRows.filter((row) => row.type === DeferredVariablePaymentType.Synthetic)
                .map((row) => row.valueAtClosing)
        ),
        [TypeOfConsideration.JvEquity]: summary.jvEquity?.row?.valueAtClosing ?? null,
        [TypeOfConsideration.CorporateEquity]: null,
        [TypeOfConsideration.PreferredEquity]: preferred?.valueAtClosing ?? null,
        [TypeOfConsideration.CommonEquity]: common?.valueAtClosing ?? null,
        [TypeOfConsideration.Other]: summary.other ?? null,
    };
};
